import React, { useState, useEffect } from "react";
import RecruiterLogo from "../assets/RecruiterLogo.png";
import { Trash, SendHorizontal } from "lucide-react";
import MphLogo from "../assets/MphLogo.png";
import { FiMail } from "react-icons/fi";
import unifiedAuthManager from "../services/unifiedAuthManager";
import { FiInfo } from "react-icons/fi";

const toTitleCase = (str) => {
  try {
    if (!str) return "";
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  } catch (error) {
    console.error("toTitleCase error:", error);
    return "";
  }
};

const EmailModal = ({
  isOpen,
  onClose,
  candidateName,
  candidateId,
  candidateEmail,
  jobTitle,
  jobLocation,
  jobOpeningDate,
  jobDuration,
  jobId,
  mphJobId,
  content,
  onSend,
  sending,
  sent,
  error,
  alreadySentCandidates = [],
  isEmailSent = false,
  emailSentOn = null,
  emailSentBy = null,
  emailSentByFirstName = null,
  emailSentByLastName = null,
  emailSentByEmail = null,
  type = "default",
}) => {
  const user = unifiedAuthManager.getUnifiedUser();
  const recruiterName = user
    ? `${toTitleCase(user?.displayName)}`
    : "Recruiter";
  let defaultMailContent = "";
  let defaultSubject = `MPH Global is Hiring: ${jobTitle || "-"} Role – ${jobLocation || "-"}`;
  let defaultTableData = [];
  if (type === "default") {
    defaultMailContent = `Hi ${candidateName || "Sir/Madam"},

This is ${recruiterName || "the recruiter"} from MPH, one of the leading technical recruitment agencies which has been operating for over 40 years. 

We are a part of MPH Global Services (https://mphexperts.com/homepage/jobs-page/).

We provide technical assistance to several industries/sectors (Oil and Gas, Nuclear, Transportation, Defense and Renewables).

Our client is looking for a ${jobTitle || "N/A"} with the following details:

LOCATION: ${jobLocation || "N/A"}
STARTING DATE: ${jobOpeningDate || "N/A"}
DURATION: ${jobDuration || "N/A"}

Should you be interested in this position please send us the below information.

Please note that it is mandatory to fill all fields in the below table.`;

    defaultTableData = [
      {
        label: "Full Name (First name LASTNAME)",
        value: "",
        deleteable: false,
      },
      { label: "Date of Birth (dd/mm/yyyy)", value: "", deleteable: false },
      { label: "Age", value: "", deleteable: false },
      { label: "Nationality", value: "", deleteable: false },
      {
        label: "Current / Latest Salary Package",
        value: "",
        deleteable: false,
      },
      {
        label: "Expected Salary in USD / per working DAY",
        value: "",
        deleteable: false,
      },
      { label: "Notice Period / Availability", value: "", deleteable: false },
      {
        label: "Current Location (city, country)",
        value: "",
        deleteable: false,
      },
      { label: "Current Position", value: "", deleteable: false },
      {
        label: "Point of Mobilization (airport of origin)",
        value: "",
        deleteable: false,
      },
      { label: "Email ID", value: "", deleteable: false },
      { label: "Mobile Number", value: "", deleteable: false },
      { label: "Total years of work experience", value: "", deleteable: false },
    ];
  } else {
    defaultMailContent = `
Hi ${candidateName || "Sir/Madam"},

This is ${recruiterName || "the recruiter"} from MPH.
We have an exciting opportunity for "Job - JOXXXXXX" that aligns well with your experience. 
If you are interested kindly acknowledge this email so that we can contact, you with further details on the role.

Regards,
${recruiterName}
MPH Team
    `;
    defaultSubject = "Exciting Opportunity Matching Your Experience – MPH";
  }

  const [isEditing, setIsEditing] = useState(false);
  const [isSubjectEditing, setIsSubjectEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [emailBody, setEmailBody] = useState(defaultMailContent);
  const [originalTableData, setOriginalTableData] = useState(defaultTableData);
  const [emailSubject, setEmailSubject] = useState(defaultSubject);
  const [tableData, setTableData] = useState(defaultTableData);
  const [isModified, setIsModified] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [hasUserEdited, setHasUserEdited] = useState(false);

  const isGenericUntouched = type && type !== "default" && !hasUserEdited;

  useEffect(() => {
    const isSame =
      JSON.stringify(originalTableData) === JSON.stringify(tableData) &&
      defaultMailContent === emailBody;
    setIsModified(!isSame);
  }, [tableData, originalTableData, emailBody]);

  // Reset local compose view when isEmailSent or sent change
  useEffect(() => {
    if (!isEmailSent) setShowCompose(false);
  }, [isEmailSent]);

  useEffect(() => {
    if (sent) setShowCompose(false);
  }, [sent]);

  const handleChange = (index, newValue) => {
    const updated = [...tableData];
    updated[index].value = newValue;
    setTableData(updated);
  };

  const handleLabelChange = (index, newLabel) => {
    const updated = [...tableData];
    updated[index].label = newLabel;
    setTableData(updated);
  };

  const addRow = () =>
    setTableData([...tableData, { label: "", value: "", deleteable: true }]);
  const removeRow = (index) =>
    setTableData(tableData.filter((_, i) => i !== index));

  const handleSaveEdit = () => {
    setIsEditing(false);
    setHasChanges(true);
    // Remove rows where label is empty (trimmed)
    setTableData((prevData) =>
      prevData.filter((row) => row.label.trim() !== ""),
    );
    handleSend();
  };
  const handleSend = () => {
    // Filter out rows where label is empty or undefined
    const validTableData = Array.isArray(tableData)
      ? tableData.filter((item) => item?.label?.trim() !== "")
      : [];

    // Build your object
    const emailData = {
      job_description_id: jobId,
      candidate_id: candidateId,
      candidate_email: candidateEmail,
      mph_jobid: mphJobId,
      subject: emailSubject,
      email_body: emailBody,
      // ✅ Add attachment only if valid records exist
      ...(validTableData.length > 0 && {
        attachment: JSON.stringify(validTableData),
      }),
      sender_email: user.email,
    };

    console.log("Email data ready to send to candidate : ", emailData);
    onSend(emailData);
  };
  if (!isOpen) return null;

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  };

  const getAlreadySentMessagePrefix = () => {
    const senderFirstName = String(emailSentByFirstName || "").trim();
    const senderLastName = String(emailSentByLastName || "").trim();
    const senderEmail = String(emailSentByEmail || "").trim();
    const normalizedSenderEmail = senderEmail.toLowerCase();
    const normalizedCurrentUserEmail = String(user?.email || "")
      .trim()
      .toLowerCase();

    if (
      normalizedSenderEmail &&
      normalizedCurrentUserEmail &&
      normalizedSenderEmail === normalizedCurrentUserEmail
    ) {
      return "An email has already been sent by you on";
    }

    const senderName = [senderFirstName, senderLastName]
      .filter(Boolean)
      .join(" ")
      .trim();

    if (senderName) {
      return `An email has already been sent by ${toTitleCase(senderName)} on`;
    }

    if (senderEmail) {
      return `An email has already been sent by ${senderEmail} on`;
    }

    if (emailSentBy) {
      return "An email has already been sent previously on";
    }

    return "An email has already been sent on";
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 anim-slideDown"
      onClick={onClose}
    >
      <div
        className={`bg-white max-h-[80vh] rounded-xl shadow-lg relative flex flex-col ${
          isEmailSent && alreadySentCandidates.length < 1 && !showCompose
            ? "w-[500px]"
            : "w-3/4"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky Header */}
        <div className="bg-white sticky top-0 z-10 p-4 pb-2 border-b rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {isEmailSent && (
                <div className="flex items-center gap-4">
                  <img src={MphLogo} alt="MPH Company Logo" className="h-5" />
                  <img
                    src={RecruiterLogo}
                    alt="Recruiter Logo"
                    className="w-8 mb-1"
                  />
                </div>
              )}
              {!isEmailSent && (
                <h3 className="text-lg font-semibold mph-primary">Email</h3>
              )}
            </div>

            <button
              className="text-gray-66 hover:text-gray-333 bg-gray-100 rounded-full p-1"
              onClick={onClose}
              type="button"
              aria-label="Close email modal"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M6 6L14 14M14 6L6 14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
          {(!isEmailSent || showCompose) && (
            <>
              <p className="text-xs text-gray-666 mt-2">
                <span className="font-semibold">To:</span>{" "}
                {Array.isArray(candidateEmail) ? (
                  candidateEmail.length > 0 ? (
                    candidateEmail.join(", ")
                  ) : (
                    <span className="italic text-gray-666">
                      [Candidate's Email]
                    </span>
                  )
                ) : (
                  candidateEmail || (
                    <span className="italic text-gray-666">
                      [Candidate's Email]
                    </span>
                  )
                )}
              </p>

              <p className="text-xs text-gray-666 mt-2">
                <span className="font-semibold">Subject:</span>{" "}
                {isSubjectEditing ? (
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => {
                      setEmailSubject(e.target.value);
                      setHasUserEdited(true);
                    }}
                    onBlur={() => setIsSubjectEditing(false)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setIsSubjectEditing(false);
                      if (e.key === "Escape") setIsSubjectEditing(false);
                    }}
                    className="italic text-gray-666 border border-gray-300 rounded px-2 py-0.5 text-xs min-w-[320px]"
                    autoFocus
                  />
                ) : (
                  <span
                    className="italic text-gray-666 cursor-text inline-block min-w-[320px]"
                    onClick={() => setIsSubjectEditing(true)}
                    title="Click to edit subject"
                  >
                    {emailSubject?.trim()
                      ? emailSubject
                      : "Click to add subject"}
                  </span>
                )}
              </p>
              {type && type !== "default" && (
                <div className="flex items-start text-[10px] text-blue-500 mt-2">
                  <FiInfo className="mr-1 mt-[2px]" size={12} />
                  <p>
                    Kindly update the job title and reference number before
                    sending the email.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Scrollable Content */}
        <div
          className={`overflow-y-auto p-4 flex-1 ${
            type === "generic" && !isEmailSent ? "min-h-[12rem]" : ""
          }`}
        >
          {(!isEmailSent || showCompose) && (
            <>
              <div className="bg-gray-50 rounded p-4 text-sm text-gray-700  ">
                {isEditing ? (
                  <textarea
                    value={emailBody}
                    onChange={(e) => {
                      setEmailBody(e.target.value);
                      setHasUserEdited(true);
                    }}
                    className="w-full h-full min-h-[12rem] border border-gray-300 rounded p-2 text-gray-700"
                  />
                ) : (
                  <div
                    className="whitespace-pre-line min-h-[6rem] cursor-text"
                    onClick={() => setIsEditing(true)}
                    title="Click to edit email body"
                  >
                    {emailBody?.trim()
                      ? emailBody
                      : "Click to add email content"}
                  </div>
                )}
                {type === "default" && (
                  <>
                    <table className="mt-4 w-full border border-gray-300 text-sm">
                      <tbody>
                        {tableData.map((row, index) => (
                          <tr key={index} className="border-b border-gray-300">
                            <td className="p-2 font-medium w-1/2">
                              {isEditing ? (
                                <input
                                  disabled={!row.deleteable}
                                  type="text"
                                  value={row.label}
                                  onChange={(e) =>
                                    handleLabelChange(index, e.target.value)
                                  }
                                  placeholder="Field name"
                                  className="w-full border border-gray-300 rounded p-1 text-gray-700"
                                />
                              ) : (
                                <span>{row.label}</span>
                              )}
                            </td>
                            <td className="p-2 w-1/2">
                              {isEditing ? (
                                <input
                                  disabled
                                  type="text"
                                  value={row.value}
                                  onChange={(e) =>
                                    handleChange(index, e.target.value)
                                  }
                                  placeholder="Value"
                                  className="w-full border border-gray-300 rounded p-1 text-gray-700"
                                />
                              ) : (
                                <span>{row.value || "—"}</span>
                              )}
                            </td>
                            {isEditing && row?.deleteable && (
                              <td className="pr-2 pb-2 pt-2">
                                <button
                                  title="Delete Row"
                                  onClick={() => removeRow(index)}
                                  className="text-red-500 hover:underline text-xs"
                                >
                                  <Trash size={12} />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <button
                      onClick={() => {
                        setIsEditing(true);
                        addRow();
                      }}
                      className="mt-3 px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs"
                    >
                      + Add Row
                    </button>
                    <span className="flex text-gray-666 mt-3">Regards,</span>

                    {type == "default" && (
                      <span className="text-gray-666 flex">
                        {recruiterName}
                      </span>
                    )}
                    <span className="text-gray-666 flex">MPH Team</span>
                  </>
                )}
              </div>

              {error && (
                <div className="text-red-500 text-xs mt-2">{error}</div>
              )}
            </>
          )}
          {isEmailSent && type === "default" && !showCompose && (
            <div className="py-3">
              <h4 className="text-sm font-semibold text-gray-600">
                {getAlreadySentMessagePrefix()}{" "}
                <span className="font-bold text-gray-800">
                  {formatDateTime(emailSentOn)}
                </span>
                .
              </h4>
              <div className="flex items-center justify-end mt-4">
                <button
                  onClick={() => setShowCompose(true)}
                  disabled={sending}
                  className={`px-4 py-2 rounded-full text-white text-sm ${
                    sending ? "bg-gray-300 cursor-not-allowed" : "profiler-bg"
                  }`}
                >
                  {sending ? "Sending..." : "Send Again"}
                </button>
              </div>
            </div>
          )}

          {isEmailSent && type !== "default" && !showCompose && (
            <div className="py-3">
              {Array.isArray(alreadySentCandidates) &&
              alreadySentCandidates.length > 0 ? (
                <>
                  <table className="w-full text-sm mb-4">
                    <thead>
                      <tr>
                        <th className="text-left font-medium">Email</th>
                        <th className="text-left font-medium">Sent On</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alreadySentCandidates.map((c, i) => (
                        <tr key={i} className="border-t">
                          <td className="py-2 font-medium">{c.email || "—"}</td>
                          <td className="py-2 font-medium">
                            {c.emailSentOn
                              ? formatDateTime(c.emailSentOn)
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="text-sm text-gray-600 mb-3">
                    The candidates listed above have a previous sent email
                    record. Do you want to send the email again?
                  </div>
                </>
              ) : (
                <div className="text-sm text-gray-600 mb-3">
                  {getAlreadySentMessagePrefix()}{" "}
                  <span className="font-bold">
                    {emailSentOn ? formatDateTime(emailSentOn) : "N/A"}
                  </span>
                  . Would you like to send it again?
                </div>
              )}

              <div className="flex items-center justify-end">
                <button
                  onClick={() => setShowCompose(true)}
                  disabled={sending}
                  className={`px-4 py-2 rounded-full text-white text-sm ${
                    sending ? "bg-gray-300 cursor-not-allowed" : "profiler-bg"
                  }`}
                >
                  {sending ? "Sending..." : "Send Again"}
                </button>
              </div>
            </div>
          )}
        </div>
        {/* Sticky Footer */}
        {(!isEmailSent || showCompose) && (
          <div className="sticky bottom-0 bg-white border-t p-4 rounded-b-xl">
            <div className="flex justify-end gap-2">
              <button
                className={`px-4 py-2 text-sm rounded-full text-white font-semibold flex items-center gap-2 transition-colors ${
                  emailBody?.trim() === "" || isGenericUntouched
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "profiler-bg hover:opacity-90 cursor-pointer"
                }`}
                onClick={() => handleSaveEdit()}
                disabled={
                  sending || emailBody?.trim() === "" || isGenericUntouched
                }
                title={
                  isGenericUntouched
                    ? "Please update the job title and reference number before sending"
                    : isEditing
                      ? "Finish editing before sending the email"
                      : "Send Email"
                }
              >
                {sent ? (
                  "Sent!"
                ) : sending ? (
                  "Sending..."
                ) : (
                  <>
                    {" "}
                    Send
                    <SendHorizontal size={14} className="inline-block" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailModal;
